import {
  forwardRef,
  useState,
  type ButtonHTMLAttributes,
  type CSSProperties,
  type ReactNode,
} from "react";
import { BRAND } from "@/lib/tokens";
import { Icon, type IconName } from "@/components/ui/Icon";

export type ButtonVariant = "primary" | "accent" | "secondary" | "ghost" | "destructive";
export type ButtonSize = "sm" | "md" | "lg";

interface SizeSpec {
  h: number;
  px: number;
  fs: number;
  gap: number;
}

const SIZES: Record<ButtonSize, SizeSpec> = {
  sm: { h: 28, px: 10, fs: 12.5, gap: 6 },
  md: { h: 34, px: 14, fs: 13.5, gap: 8 },
  lg: { h: 40, px: 18, fs: 14.5, gap: 8 },
};

interface VariantSpec {
  bg: string;
  color: string;
  border: string;
  hover: string;
}

const VARIANTS: Record<ButtonVariant, VariantSpec> = {
  primary: { bg: BRAND.blue, color: "#fff", border: "transparent", hover: "#004a82" },
  accent: { bg: BRAND.orange, color: "#fff", border: "transparent", hover: "#E5731A" },
  secondary: {
    bg: "var(--surface)",
    color: "var(--ink-1)",
    border: "var(--hairline-strong)",
    hover: "var(--surface-soft)",
  },
  ghost: {
    bg: "transparent",
    color: "var(--ink-1)",
    border: "transparent",
    hover: "var(--surface-soft)",
  },
  destructive: {
    bg: "var(--surface)",
    color: "var(--danger)",
    border: "rgba(126,47,38,0.25)",
    hover: "#FCEEEC",
  },
};

interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: IconName;
  iconRight?: IconName;
  children?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "secondary",
    size = "md",
    icon,
    iconRight,
    children,
    disabled,
    style,
    onClick,
    ...rest
  },
  ref,
) {
  const sz = SIZES[size];
  const v = VARIANTS[variant];
  const [hover, setHover] = useState(false);
  const buttonStyle: CSSProperties = {
    height: sz.h,
    padding: `0 ${sz.px}px`,
    gap: sz.gap,
    fontSize: sz.fs,
    background: hover && !disabled ? v.hover : v.bg,
    color: v.color,
    border: `1px solid ${v.border}`,
    borderRadius: 8,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 500,
    fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.5 : 1,
    transition: "background .12s",
    whiteSpace: "nowrap",
    boxShadow:
      variant === "primary" || variant === "accent"
        ? "0 1px 0 rgba(0,0,0,0.04), 0 1px 2px rgba(13,62,99,0.08)"
        : "none",
    ...style,
  };
  return (
    <button
      ref={ref}
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={buttonStyle}
      {...rest}
    >
      {icon ? <Icon name={icon} size={sz.fs + 2} /> : null}
      {children}
      {iconRight ? <Icon name={iconRight} size={sz.fs + 2} /> : null}
    </button>
  );
});

interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
  icon: IconName;
  size?: number;
  active?: boolean;
  color?: string;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { icon, size = 32, active = false, color, onClick, style, title, ...rest },
  ref,
) {
  const [hover, setHover] = useState(false);
  return (
    <button
      ref={ref}
      onClick={onClick}
      title={title}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        width: size,
        height: size,
        borderRadius: 8,
        border: "none",
        background: active ? "var(--surface-elev)" : hover ? "var(--surface-soft)" : "transparent",
        color: color ?? "var(--ink-1)",
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "background .12s",
        flexShrink: 0,
        ...style,
      }}
      {...rest}
    >
      <Icon name={icon} size={size >= 40 ? 20 : size >= 32 ? 17 : 15} />
    </button>
  );
});
